import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Sun, CloudRain, Coins, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { activitiesAPI } from '@/lib/api';

const MOOD_EMOJIS = [
  '😭', '😞', '😔', '🙁', '😕', 
  '😐', '🙂', '😊', '😄', '😁', '🤩'
];

const MOOD_LABELS = [
  'Péssimo', 'Muito mal', 'Mal', 'Desanimado', 'Preocupado',
  'Neutro', 'Ok', 'Bem', 'Muito bem', 'Ótimo', 'Excelente'
];

export function CheckinModal({ open, onClose, onComplete }) {
  const { refreshUser } = useAuth();
  const [moodScore, setMoodScore] = useState([5]);
  const [hadCraving, setHadCraving] = useState(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [tokensEarned, setTokensEarned] = useState(0);

  const handleSubmit = async () => {
    if (hadCraving === null) {
      toast.error('Por favor, responda se teve fissura hoje.');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await activitiesAPI.submitCheckin({
        mood_score: moodScore[0],
        had_craving: hadCraving,
        notes: notes.trim() || null
      });

      setTokensEarned(response.data.tokens_earned);
      setShowSuccess(true);
      
      setTimeout(() => {
        refreshUser();
        onComplete && onComplete();
      }, 2000);
      
    } catch (error) {
      console.error('Failed to submit checkin:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar check-in');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!showSuccess) {
      onClose();
    }
  };

  if (showSuccess) {
    return (
      <Dialog open={open} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md">
          <div className="text-center py-8">
            <div className="text-6xl mb-4">{MOOD_EMOJIS[moodScore[0]]}</div>
            <h3 className="text-xl font-bold mb-2">Check-in Registrado!</h3>
            <p className="text-gray-600 mb-4">
              {moodScore[0] >= 7 
                ? 'Que bom que você está bem! Continue assim! 💪' 
                : moodScore[0] >= 4 
                  ? 'Um dia de cada vez. Você está no caminho certo! 🌟'
                  : 'Dias difíceis fazem parte. Você não está sozinho! 🤗'
              }
            </p>
            <div className="bg-amber-50 rounded-lg p-4 inline-flex items-center gap-2">
              <Coins className="h-5 w-5 text-amber-600" />
              <span className="font-bold text-amber-800">+{tokensEarned} tokens AWA</span>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sun className="h-5 w-5 text-amber-500" />
            Check-in Diário
          </DialogTitle>
          <DialogDescription>
            Como você está se sentindo hoje?
            <span className="flex items-center gap-1 mt-2 text-amber-600">
              <Coins className="h-4 w-4" />
              <strong>+5 tokens AWA</strong>
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-6">
          {/* Mood Slider */}
          <div>
            <div className="text-center mb-4">
              <span className="text-5xl">{MOOD_EMOJIS[moodScore[0]]}</span>
              <p className="text-lg font-medium mt-2">{MOOD_LABELS[moodScore[0]]}</p>
            </div>
            
            <Slider
              value={moodScore}
              onValueChange={setMoodScore}
              max={10}
              min={0}
              step={1}
              className="w-full"
            />
            
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>0</span>
              <span>5</span>
              <span>10</span>
            </div>
          </div>

          {/* Craving Question */}
          <div>
            <Label className="text-sm font-medium mb-3 block">
              Teve fissura ou vontade de usar hoje?
            </Label>
            <div className="flex gap-3">
              <Button
                type="button"
                variant={hadCraving === true ? 'default' : 'outline'}
                onClick={() => setHadCraving(true)}
                className={`flex-1 ${
                  hadCraving === true 
                    ? 'bg-orange-500 hover:bg-orange-600' 
                    : 'hover:border-orange-300'
                }`}
              >
                <CloudRain className="h-4 w-4 mr-2" />
                Sim
              </Button>
              <Button
                type="button"
                variant={hadCraving === false ? 'default' : 'outline'}
                onClick={() => setHadCraving(false)}
                className={`flex-1 ${
                  hadCraving === false 
                    ? 'bg-green-500 hover:bg-green-600' 
                    : 'hover:border-green-300'
                }`}
              >
                <Sun className="h-4 w-4 mr-2" />
                Não
              </Button>
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="notes" className="text-sm font-medium mb-2 block">
              Quer compartilhar algo sobre seu dia? (opcional)
            </Label>
            <Textarea
              id="notes"
              placeholder="Escreva aqui..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={500}
              rows={3}
              className="resize-none"
            />
            <p className="text-xs text-gray-400 text-right mt-1">{notes.length}/500</p>
          </div>

          {/* Submit */}
          <Button
            onClick={handleSubmit}
            disabled={hadCraving === null || submitting}
            className="w-full bg-purple-600 hover:bg-purple-700"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Check className="h-4 w-4 mr-2" />
            )}
            Registrar Check-in
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
